import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../components/Layout';
import { Tabs } from '../components/ui';
import { api } from '../lib/api';
import { useI18n, useT } from '../lib/i18n';
import type { QueueStats } from '../lib/types';
import { BlacklistTab } from './notifications/Blacklist';
import { CasesTab } from './notifications/Cases';
import { ComposeTab } from './notifications/Compose';
import { HistoryTab } from './notifications/History';
import { TemplatesTab } from './notifications/Templates';

type Tab = 'compose' | 'cases' | 'templates' | 'history' | 'blacklist';

/**
 * The notification centre.
 *
 * Two halves, and the tab order says which is which. **Compose** is the thing
 * an operator does *now* — write a message, pick who gets it, send. **Cases**
 * is the thing that keeps happening: when this occurs, wait, check they still
 * look like this, then send. Everything else supports one or the other.
 */
export function NotificationsPage() {
  const t = useT();
  const [tab, setTab] = useState<Tab>('compose');

  const queue = useQuery({
    queryKey: ['queue-stats'],
    queryFn: () => api.get<QueueStats>('/api/notifications/queue'),
    refetchInterval: 30_000,
  });

  const blacklist = useQuery({
    queryKey: ['blacklist-count'],
    queryFn: () => api.get<{ count: number }>('/api/notifications/blacklist/count'),
    staleTime: 60_000,
  });

  return (
    <>
      <PageHeader
        title={t('notif.title')}
        description={t('notif.description')}
        action={<QueueBadge stats={queue.data} />}
      />

      <Tabs
        label={t('notif.tabsLabel')}
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'compose', label: t('notif.tab.compose') },
          { id: 'cases', label: t('notif.tab.cases') },
          { id: 'templates', label: t('notif.tab.templates') },
          { id: 'history', label: t('notif.tab.history') },
          {
            id: 'blacklist',
            label: t('notif.tab.blacklist'),
            badge: blacklist.data?.count,
          },
        ]}
      />

      {tab === 'compose' && <ComposeTab onSent={() => setTab('history')} />}
      {tab === 'cases' && <CasesTab />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'history' && <HistoryTab />}
      {tab === 'blacklist' && <BlacklistTab />}
    </>
  );
}

/**
 * The queue's health, in one line.
 *
 * Only ever shows a number when something is behind — a permanently visible
 * "0 overdue" is a badge nobody looks at, and the point of this one is that
 * seeing it means something.
 */
function QueueBadge({ stats }: { stats: QueueStats | undefined }) {
  const { language, t } = useI18n();

  if (!stats) return null;

  const pending = stats.counts.pending ?? 0;

  return (
    <p className="text-xs text-ink-3">
      {stats.overdue > 0 ? (
        <span className="font-medium text-warn">
          {t('notif.queue.overdue', { count: stats.overdue })}
        </span>
      ) : (
        <>
          {t('notif.queue.scheduled', { count: pending })}
          {stats.nextDueAt &&
            ` · ${t('notif.queue.nextAt', {
              time: new Date(stats.nextDueAt).toLocaleTimeString(language),
            })}`}
        </>
      )}
    </p>
  );
}
